import styled from "@emotion/styled";
import { useCallback, useState, type ReactNode } from "react";
import useResizeObserver from "use-resize-observer";

import { DragDropProvider, MiddleClickPointerSensor, type DragDropEventHandlers } from "@skilitics/drag-drop";

import { getCanvasBackgroundCss } from "../canvas";
import { Tray } from "../tray/Tray";
import type { Point } from "../types";
import { useDimensions } from "./DimensionsContext";
import { usePan } from "./PanContext";
import { PerspectiveDraggable } from "./PerspectiveDraggable";
import { useZoom } from "./ZoomContext";

const RelativePositionContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const DraggableTransformer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  // place-items: center; // Leaving this commented as a warning to not use in Safari. The module map won't be centered!
`;

type PerspectiveTargetSizerProps = { pannable: boolean };

const PerspectiveTargetSizer = styled.div<PerspectiveTargetSizerProps>`
  cursor: ${({ pannable }) => (pannable ? "grab" : "default")};

  ${getCanvasBackgroundCss}

  position: absolute;
`;

const sensors = [MiddleClickPointerSensor];

export const PerspectiveTarget = ({
  isDraggable = false,
  children,
  disabled = false,
}: {
  isDraggable: boolean;
  children: ReactNode;
  disabled?: boolean;
}) => {
  const { width, height, padding } = useDimensions();
  const { zoom } = useZoom();
  const { scaledPan, onPanDrag } = usePan();
  const [dragDelta, setDragDelta] = useState<Point | undefined>();

  const { ref, width: containerWidth = 0, height: containerHeight = 0 } = useResizeObserver();

  // DragDropProvider passes (event, manager) to callbacks. We read the transform
  // from the manager directly because the event snapshot's transform is stale
  // when feedback: "none" is used on the draggable (see SceneDraggable for details).

  const handleDragMove: DragDropEventHandlers["onDragMove"] = useCallback((_, { dragOperation: { transform } }) => {
    if (transform) {
      setDragDelta(transform);
    }
  }, []);

  const handleDragEnd: DragDropEventHandlers["onDragEnd"] = useCallback(
    (event, { dragOperation: { transform } }) => {
      setDragDelta(undefined);

      if (event.canceled || !transform) return;
      onPanDrag({ x: scaledPan.x + transform.x, y: scaledPan.y + transform.y });
    },
    [onPanDrag, scaledPan],
  );

  return (
    <DragDropProvider onDragMove={handleDragMove} onDragEnd={handleDragEnd} sensors={sensors}>
      <PerspectiveDraggable id="perspective" position={scaledPan} dragDelta={dragDelta} disabled={disabled}>
        <DraggableTransformer ref={ref}>
          <PerspectiveTargetSizer
            pannable={isDraggable}
            style={{ transform: `scale(${zoom})`, width, height, padding: `${padding}px` }}
          >
            <RelativePositionContainer>
              <Tray containerDimensions={{ height: containerHeight, width: containerWidth }} />
              {children}
            </RelativePositionContainer>
          </PerspectiveTargetSizer>
        </DraggableTransformer>
      </PerspectiveDraggable>
    </DragDropProvider>
  );
};
